import React, { useState, useEffect } from 'react'
import { AuthorPreviewContent } from './previews/AuthorPreviewContent'
import { getApiBase } from '../../utils/api'
import { getQRBranding } from '../../utils/branding'
import { Loader } from 'lucide-react'

interface AuthorProductionWrapperProps {
  linkId?: string
  linkData?: any
  isPreview?: boolean
  accountSlug?: string
}

export const AuthorProductionWrapper: React.FC<AuthorProductionWrapperProps> = ({ 
  linkId, 
  linkData: propLinkData, 
  isPreview = false,
  accountSlug
}) => {
  const [linkData, setLinkData] = useState<any>(propLinkData || null)
  const [branding, setBranding] = useState<any>(null)
  const [loading, setLoading] = useState(!propLinkData)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!linkId || propLinkData) return

      try {
        setLoading(true)
        const API_BASE = getApiBase()
        
        // Use the appropriate endpoint based on whether we have an account slug
        const endpoint = accountSlug 
          ? `${API_BASE}/api/public/link/${accountSlug}/${linkId}`
          : `${API_BASE}/api/public/link/${linkId}`
        
        const response = await fetch(endpoint)
        
        if (!response.ok) {
          throw new Error('Link not found')
        }
        
        const data = await response.json()
        setLinkData(data)
        
        // Load branding
        const brandingConfig = await getQRBranding(data.branding_type, data.author_id)
        setBranding(brandingConfig)
      } catch (err) {
        console.error('Error loading author data:', err)
        setError('Autorenprofil konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [linkId, propLinkData, accountSlug])

  // Load branding for preview mode
  useEffect(() => {
    const loadBranding = async () => {
      if (!propLinkData || branding) return
      
      const brandingConfig = await getQRBranding(
        propLinkData.branding_type || 'beyondpages', 
        propLinkData.author_id
      )
      setBranding(brandingConfig)
    }
    
    loadBranding()
  }, [propLinkData, branding])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !linkData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">{error || 'Keine Daten verfügbar'}</p>
      </div>
    )
  }

  // Transform data for AuthorPreviewContent
  const previewData = {
    // Name ist optional: keine Fallback-Anzeige aus dem Titel
    name: linkData.metadata?.name || '',
    profession: linkData.metadata?.profession || '',
    shortBio: linkData.metadata?.shortBio || linkData.description || '',
    fullBio: linkData.metadata?.fullBio || '',
    profileImageUrl: linkData.metadata?.profileImageUrl || '',
    socialLinks: linkData.metadata?.socialLinks || {},
    contact: linkData.metadata?.contact || {},
    awards: linkData.metadata?.awards || [],
    pressKitUrl: linkData.metadata?.pressKitUrl || '',
    videoUrl: linkData.metadata?.videoUrl || '',
    newsletterEnabled: linkData.metadata?.newsletterEnabled || false,
    companyPosition: linkData.metadata?.companyPosition || 'bottom',
    fonts: linkData.metadata?.fonts || {},
    textSizes: linkData.metadata?.textSizes || {}
  }

  // Transform branding data
  const brandingData = {
    companyName: branding?.companyName || '',
    brandingType: linkData.branding_type || 'beyondpages',
    textColor: linkData.metadata?.customColors?.textColor || branding?.textColor || '#FFFFFF',
    primaryColor: linkData.metadata?.customColors?.primaryColor || branding?.primaryColor || '#3B82F6',
    secondaryColor: linkData.metadata?.customColors?.secondaryColor || branding?.secondaryColor || '#1F2937',
  }

  // Determine color mode from metadata or use default
  const colorMode = linkData.metadata?.colorMode || 'gradient'

  return (
    <AuthorPreviewContent 
      data={previewData}
      branding={brandingData}
      colorMode={colorMode}
      isPreview={isPreview}
    />
  )
}
